import CreativeOSAIIcon from '@/assets/CreativeOSAIButtonIcon'
import CreativeOSAISearchIcon from '@/assets/CreativeOSAiSearchIcon'
import CreativesIcon from '@/assets/CreativesIcon'
import SearchActionButtonIcon from '@/assets/SearchActionButton'
import { Search } from '@mynaui/icons-react'
import { ChangeEvent, useState } from 'react'
import { Stack, Text } from './custom-components'
import AgenticSearchBarAI from './custom-components/AgenticSearchBarAI'
import AgenticSearchBarCreatives from './custom-components/AgenticSearchBarCreatives'
import { Badge } from './shadcn/badge'
import { Button } from './shadcn/button'
import { Dialog, DialogContent, DialogPortal } from './shadcn/dialog'
import { Input } from './shadcn/input'

const tabs = [
  {
    label: 'Creatives',
    icon: CreativesIcon,
    searchPlaceholder: 'Pets + features and benefits',
  },
  {
    label: 'CreativeOS AI',
    icon: CreativeOSAIIcon,
    searchPlaceholder: 'Let’s find the perfect start',
  },
]
const subTabs = [
  {
    label: 'Templates',
  },
  {
    label: 'Inspirations',
  },
]

export type PermissionData = {
  ID: string
  email: string
  userCanAccessAds: boolean
  userCanAccessExperts: boolean
  usersPlans: string[]
  requestLimit: number
  limitNextUpdateAt: string
}

const AgenticSearchDialog = ({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: () => void
}) => {
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>('')
  const [selectedTab, setSelectedTab] = useState<'Creatives' | 'CreativeOS AI'>('Creatives')
  const [searchPlaceHolder, setSearchPlaceHolder] = useState<string>(tabs[0].searchPlaceholder)
  const [contentType, setContentType] = useState<'Inspirations' | 'Templates'>('Templates')

  const runSearch = () => {
    setSearchTerm('')
    setSearchTerm(debouncedSearchTerm)
  }

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault()
    setDebouncedSearchTerm(e.target.value)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      runSearch()
    }
  }

  return (
    <Dialog modal={false} open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogContent
          className={` gap-1 overflow-hidden p-0 pl-6 pt-8 h-full max-h-[75svh] lg:max-h-[49rem] !rounded-3xl w-11/12 md:max-w-[850px] lg:max-w-5xl lg:[&>button]:hidden z-[31] bg-themebackground dark:bg-themebackgrounddark flex flex-col`}
        >
          <Stack className="h-fit items-center w-full gap-6 pr-6 pb-6">
            <Text size={'xl'} weight={'medium'} className="w-fit">
              What should we create?
            </Text>

            <div className="flex gap-5">
              {tabs.map(({ label, icon: Icon, searchPlaceholder }) => (
                <Button
                  key={label}
                  variant="outline"
                  className={`flex gap-3 items-center ${
                    selectedTab === label
                      ? 'border-primary border-themedestructive text-primary text-themedestructive'
                      : ''
                  }`}
                  onClick={() => {
                    setSelectedTab(label as typeof selectedTab)
                    setSearchPlaceHolder(searchPlaceholder)
                  }}
                >
                  <div className="mr-2">
                    <Icon size={20} selected={selectedTab === label} />
                  </div>
                  {label}
                </Button>
              ))}
            </div>

            <div className="p-2 bg-themesidebar dark:bg-themesidebardark rounded-xl flex flex-col w-full gap-2">
              <div className="outline-none border-none flex gap-3 items-center w-full">
                {selectedTab == 'CreativeOS AI' ? (
                  <CreativeOSAISearchIcon />
                ) : (
                  <Search className="h-7 w-7" aria-hidden="true" />
                )}
                <Input
                  type="text"
                  value={debouncedSearchTerm}
                  onChange={handleSearchChange}
                  onKeyDown={handleKeyDown}
                  placeholder={searchPlaceHolder}
                  className="p-0 lg:w-full outline-none border-0 focus-visible:ring-offset-0 focus-visible:ring-0 shadow-none"
                />
                <div onClick={runSearch} className="hover:cursor-pointer">
                  <SearchActionButtonIcon size={30} />
                </div>
              </div>
              {selectedTab == 'CreativeOS AI' ? (
                <Stack className="gap-2">
                  <div className="bg-[#eeeeee] w-full h-[1px]" />

                  <div className="flex gap-2">
                    {subTabs.map(({ label }) => (
                      <Badge
                        key={label}
                        variant={contentType === label ? 'secondary' : 'outline'}
                        className={`rounded-full text-thememutedforeground ${
                          contentType === label
                            ? 'bg-themebackground dark:bg-themebackground text-themeforeground dark:text-themeprimaryforeground'
                            : ''
                        } border-none hover:cursor-pointer`}
                        onClick={() => {
                          setContentType(label as typeof contentType)
                        }}
                      >
                        {label}
                      </Badge>
                    ))}
                  </div>
                </Stack>
              ) : null}
            </div>
          </Stack>

          {selectedTab == 'Creatives' ? (
            <AgenticSearchBarCreatives
              debouncedSearchTerm={debouncedSearchTerm}
              searchTerm={searchTerm}
              setDebouncedSearchTerm={setDebouncedSearchTerm}
            />
          ) : selectedTab == 'CreativeOS AI' ? (
            <AgenticSearchBarAI
              searchTerm={searchTerm}
              type={contentType == 'Templates' ? 'templates' : 'inspirations'}
            />
          ) : null}
        </DialogContent>

        <div className="fixed inset-0 bg-black/10 backdrop-blur-sm z-[30]" onClick={onOpenChange} />
      </DialogPortal>
    </Dialog>
  )
}

export default AgenticSearchDialog
